import React, { useEffect, useRef } from 'react';

/**
 * Transcript — scrolling conversation display.
 *
 * Props:
 *   history          — array of { role: 'user'|'assistant', content: string }
 *   partial          — current partial user transcript (from Deepgram interim results)
 *   streamedResponse — current partial assistant response (from Claude streaming)
 */
/**
 * Render assistant text, splitting out any email-draft block into a readable
 * card. Handles the canonical <<DRAFT>>...<<END_DRAFT>> block and the legacy
 * [EMAIL_DRAFT]...[/EMAIL_DRAFT] block. The block stays visible in the
 * transcript (it is only stripped before TTS), so Dave can review what was
 * saved to his Gmail drafts.
 */
function renderAssistantText(text) {
  if (typeof text !== 'string') return text;
  const m = /<<\s*DRAFT\s*>>([\s\S]*?)<<\s*(?:END_DRAFT|DRAFT_END|\/DRAFT)\s*>>|\[EMAIL_DRAFT\]([\s\S]*?)\[\/EMAIL_DRAFT\]/i.exec(text);
  if (!m) return text;
  const before = text.slice(0, m.index).trim();
  const draft = (m[1] || m[2] || '').trim();
  const after = text.slice(m.index + m[0].length).trim();
  return (
    <>
      {before && <div>{before}</div>}
      <div className="email-draft-card">
        <div className="email-draft-card-label">&#9993; Email draft &mdash; saved to Gmail</div>
        <pre className="email-draft-card-body">{draft}</pre>
      </div>
      {after && <div style={{ marginTop: 6 }}>{after}</div>}
    </>
  );
}

export default function Transcript({ history = [], partial = '', streamedResponse = '' }) {
  const containerRef = useRef(null);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [history, partial, streamedResponse]);

  if (history.length === 0 && !partial && !streamedResponse) {
    return null;
  }

  return (
    <div className="transcript-container" ref={containerRef}>
      {history.map((msg, i) => {
        let displayContent = msg.content;
        if (Array.isArray(msg.content)) {
          const textPart = msg.content.find(c => c.type === 'text');
          const hasImage = msg.content.some(c => c.type === 'image');
          displayContent = (
            <>
              {hasImage && <span style={{ marginRight: 6, opacity: 0.6 }}>&#128247;</span>}
              {textPart?.text || '[image attached]'}
            </>
          );
        } else if (msg.role === 'assistant') {
          displayContent = renderAssistantText(msg.content);
        }
        return (
          <div key={i} className={`message ${msg.role}`}>
            {displayContent}
          </div>
        );
      })}

      {/* Show partial user transcript while they're speaking */}
      {partial && (
        <div className="message user partial">
          {partial}
        </div>
      )}

      {/* Show streamed assistant response only while generating (before it's in history) */}
      {streamedResponse && !history.some(h => h.role === 'assistant' && (typeof h.content === 'string' ? h.content : '') === streamedResponse) && (
        <div className="message assistant partial">
          {streamedResponse}
        </div>
      )}
    </div>
  );
}
